/**
 * Pure selection logic for the destination tree.
 *
 * The canonical state is a "Selection Frontier": the set of destination ids the
 * traveller ticked, with the invariant that no member is an ancestor or
 * descendant of another. Everything the UI shows — checkbox state, count
 * badges, bubbles, the search payload — is DERIVED from it. Nothing about
 * selection is stored twice, which is what previously allowed the visual state
 * and the canonical state to drift apart (FCST-8659, FCST-8662).
 *
 * See docs/adr/0001-selection-frontier-without-roll-up.md. Note especially that
 * we never roll a complete set of children up to their parent: the widget's tree
 * is pruned relative to what search knows, so a parent covers destinations that
 * are not offered as children, and rolling up would silently widen the search.
 *
 * This module is deliberately DOM-free so it can be unit tested without a modal,
 * and so the build can apply the same rules (see filters/offered-destination-ids).
 */
import type { ProductGroup } from '@search/api/src/types';

export interface DestinationNode {
  id: number;
  name: string;
  children?: DestinationNode[];
}

/** Derived display state of a node's checkbox. */
export type NodeSelectionState = 'checked' | 'mixed' | 'unchecked';

export interface DestinationIndex {
  byId: Map<number, DestinationNode>;
  parentOf: Map<number, number | null>;
  childIdsOf: Map<number, number[]>;
  /** Ancestors ordered nearest-first. */
  ancestorsOf: Map<number, number[]>;
  descendantsOf: Map<number, number[]>;
}

export type Frontier = ReadonlySet<number>;

/**
 * The search filters' destination tree (`productGroups`), as the tree can use
 * it. ProductGroup (LturProductGroup_model) declares id/name/productGroups as
 * optional|null, but the tree can only operate on groups that carry an id and
 * name, so any other group is left out, with its descendants — see the
 * handle-nullable-schema-types skill.
 */
export const toDestinationNodes = (groups: ProductGroup[]): DestinationNode[] =>
  groups
    .filter(
      (g): g is ProductGroup & { id: number; name: string } =>
        g.id != null && g.name != null
    )
    .map(g => ({
      id: g.id,
      name: g.name,
      children: g.productGroups
        ? toDestinationNodes(g.productGroups)
        : undefined,
    }));

/**
 * Flatten the tree once into lookup maps. Callers should build this a single
 * time and reuse it — every other function here is a cheap read against it.
 */
export function buildIndex(roots: DestinationNode[]): DestinationIndex {
  const index: DestinationIndex = {
    byId: new Map(),
    parentOf: new Map(),
    childIdsOf: new Map(),
    ancestorsOf: new Map(),
    descendantsOf: new Map(),
  };

  const walk = (node: DestinationNode, ancestors: number[]): number[] => {
    index.byId.set(node.id, node);
    index.parentOf.set(node.id, ancestors[0] ?? null);
    index.ancestorsOf.set(node.id, ancestors);

    const children = node.children ?? [];
    index.childIdsOf.set(
      node.id,
      children.map(child => child.id)
    );

    const nextAncestors = [node.id, ...ancestors];
    const descendants: number[] = [];
    for (const child of children) {
      descendants.push(child.id, ...walk(child, nextAncestors));
    }
    index.descendantsOf.set(node.id, descendants);

    return descendants;
  };

  for (const root of roots) walk(root, []);

  return index;
}

const ancestorsOf = (index: DestinationIndex, id: number): number[] =>
  index.ancestorsOf.get(id) ?? [];

const descendantsOf = (index: DestinationIndex, id: number): number[] =>
  index.descendantsOf.get(id) ?? [];

/** True when no frontier member is an ancestor of another. */
export function isValidFrontier(
  index: DestinationIndex,
  frontier: Frontier
): boolean {
  for (const id of frontier) {
    if (ancestorsOf(index, id).some(ancestor => frontier.has(ancestor))) {
      return false;
    }
  }
  return true;
}

/**
 * The single ingest point for externally supplied selections — CMS defaults,
 * free-text picks, query strings. Drops unknown ids and any id already covered
 * by an ancestor, so the invariant holds regardless of what the caller passes.
 */
export function normaliseFrontier(
  index: DestinationIndex,
  ids: Iterable<number>
): Set<number> {
  const known = [...ids].filter(id => index.byId.has(id));
  const candidates = new Set(known);
  const frontier = new Set<number>();

  for (const id of known) {
    const covered = ancestorsOf(index, id).some(ancestor =>
      candidates.has(ancestor)
    );
    if (!covered) frontier.add(id);
  }

  return frontier;
}

export function selectionStateOf(
  index: DestinationIndex,
  frontier: Frontier,
  id: number
): NodeSelectionState {
  if (frontier.has(id)) return 'checked';
  if (ancestorsOf(index, id).some(ancestor => frontier.has(ancestor))) {
    return 'checked';
  }
  if (descendantsOf(index, id).some(descendant => frontier.has(descendant))) {
    return 'mixed';
  }
  return 'unchecked';
}

/**
 * How many direct children are selected or partially selected — the number
 * rendered in the count badge on a Mixed parent.
 */
export function selectedChildCount(
  index: DestinationIndex,
  frontier: Frontier,
  id: number
): number {
  const children = index.childIdsOf.get(id) ?? [];
  return children.filter(
    child => selectionStateOf(index, frontier, child) !== 'unchecked'
  ).length;
}

/**
 * Replace an ancestor selection with the cover of everything it selected
 * EXCEPT the branch leading to `excludedId`.
 *
 * Walking the path down from the covering ancestor and adding each level's
 * other children is what turns "Boa Vista, minus Praia de Chaves" into
 * "Praia Da Cruz". Note this is lossy where the tree is pruned — see the ADR.
 */
function expandCoveringAncestor(
  index: DestinationIndex,
  frontier: Set<number>,
  excludedId: number
): Set<number> {
  const ancestors = ancestorsOf(index, excludedId);
  const covering = ancestors.find(ancestor => frontier.has(ancestor));
  if (covering === undefined) return frontier;

  frontier.delete(covering);

  const between = ancestors.slice(0, ancestors.indexOf(covering)).reverse();
  const path = [covering, ...between, excludedId];

  for (let level = 0; level < path.length - 1; level++) {
    const nextOnPath = path[level + 1];
    for (const child of index.childIdsOf.get(path[level]) ?? []) {
      if (child !== nextOnPath) frontier.add(child);
    }
  }

  return frontier;
}

/**
 * Apply a click on `id` and return the next frontier. Never mutates the input.
 *
 * - unchecked → select it, replacing anything it covers and any ancestor
 * - mixed     → clear its whole subtree (matches the results-page filter)
 * - checked   → deselect it, expanding a covering ancestor if that is what
 *               selected it
 */
export function toggleNode(
  index: DestinationIndex,
  frontier: Frontier,
  id: number
): Set<number> {
  const next = new Set(frontier);
  if (!index.byId.has(id)) return next;

  const state = selectionStateOf(index, frontier, id);

  if (state === 'unchecked') {
    // No ancestor or descendant can be in the frontier here — either would have
    // made the state 'checked' or 'mixed' — so adding is sufficient. Validity of
    // the incoming frontier is normaliseFrontier's job, not this function's.
    next.add(id);
    return next;
  }

  if (state === 'mixed') {
    for (const descendant of descendantsOf(index, id)) next.delete(descendant);
    next.delete(id);
    return next;
  }

  if (next.delete(id)) return next;

  return expandCoveringAncestor(index, next, id);
}

export interface SelectionItem {
  id: number;
  name: string;
  isHotel?: boolean;
}

/**
 * Selections that are not nodes in the tree: accommodations (Giata ids), and
 * destinations the pruned feed omits. They carry their own name because it
 * cannot be resolved from the index.
 */
export type OffTreeSelections = Map<number, { name: string; isHotel: boolean }>;

export interface PartitionedSelection {
  frontier: Set<number>;
  offTree: OffTreeSelections;
}

/**
 * Split incoming selections into the normalised frontier and everything the tree
 * does not contain.
 *
 * `isHotel` is the ONLY discriminator between a destination and an accommodation.
 * Tree membership must never be used for that: the feed is pruned, so free-text
 * search legitimately returns destination ids that are absent from the tree
 * (Sal Rei, 20212, is a live example) and those must still be searched as
 * destinations, not as Giata ids.
 */
export function partitionSelection(
  index: DestinationIndex,
  items: SelectionItem[]
): PartitionedSelection {
  const inTree: number[] = [];
  const offTree: OffTreeSelections = new Map();

  for (const item of items) {
    if (!item.isHotel && index.byId.has(item.id)) {
      inTree.push(item.id);
    } else {
      offTree.set(item.id, { name: item.name, isHotel: item.isHotel === true });
    }
  }

  // An id belongs to exactly one namespace. Giata ids and product group ids are
  // independent sequences, so the same number can legitimately mean both a hotel
  // and a destination; isHotel wins, otherwise the id would be emitted as both
  // ?destinations= and ?accommodations= and over-constrain the search.
  const frontier = normaliseFrontier(
    index,
    inTree.filter(id => !offTree.has(id))
  );

  return { frontier, offTree };
}

/** Bubbles, in frontier order. Unknown ids are skipped. */
export function frontierToSelection(
  index: DestinationIndex,
  frontier: Frontier
): Array<{ id: number; name: string }> {
  const selection: Array<{ id: number; name: string }> = [];
  for (const id of frontier) {
    const node = index.byId.get(id);
    if (node) selection.push({ id, name: node.name });
  }
  return selection;
}
